import {changeIssuesTabCounter} from '@github-ui/issues-tab-counter'
import type {BulkActionQueryTarget} from '@github-ui/item-picker-base/ItemPicker'
import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceSelectorProxy} from 'relay-runtime'

import type {updateIssuesBulkActionsByQueryMutation} from './__generated__/updateIssuesBulkActionsByQueryMutation.graphql'
import type {
  IssueClosedStateReason,
  IssueState,
  updateIssuesBulkActionsMutation,
  UpdateIssuesBulkInput,
} from './__generated__/updateIssuesBulkActionsMutation.graphql'

type issueUpdateArgs = {
  state: IssueState
  stateReason?: IssueClosedStateReason
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const updateIssuesBulkMutation = graphql`
  mutation updateIssuesBulkActionsMutation($input: UpdateIssuesBulkInput!) @raw_response_type {
    updateIssuesBulk(input: $input) {
      jobId
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const updateIssuesBulkByQueryMutation = graphql`
  mutation updateIssuesBulkActionsByQueryMutation($input: UpdateIssuesBulkByQueryInput!) @raw_response_type {
    updateIssuesBulkByQuery(input: $input) {
      jobId
    }
  }
`

function updateIssuesById(store: RecordSourceSelectorProxy, ids: string[], issueUpdateArgs: issueUpdateArgs) {
  for (const id of ids) {
    const issue = store.get(id)
    if (issue) {
      for (const [key, value] of Object.entries(issueUpdateArgs)) {
        issue.setValue(value, key)
      }
    }
  }
}

export function commitUpdateIssueBulkMutation({
  environment,
  optimisticUpdateIds,
  input: {ids, queryTarget, state, stateReason},
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {ids: string[]; queryTarget?: BulkActionQueryTarget} & issueUpdateArgs
  optimisticUpdateIds: string[]
  onCompleted?: (jobId?: string) => void
  onError?: (error: Error) => void
}) {
  const inputArgs = {state, stateReason}
  const inputHash: UpdateIssuesBulkInput = {
    ids,
    ...inputArgs,
  }

  let tabCounterDelta = 0

  const updater = (store: RecordSourceSelectorProxy) => updateIssuesById(store, optimisticUpdateIds, inputArgs)
  const optimisticUpdater = (store: RecordSourceSelectorProxy) => {
    if (!queryTarget) {
      for (const id of optimisticUpdateIds) {
        const issue = store.get(id)
        if (issue) {
          const currentState = issue.getValue('state')
          if (currentState === 'OPEN' && state === 'CLOSED') tabCounterDelta--
          else if (currentState === 'CLOSED' && state === 'OPEN') tabCounterDelta++
        }
      }
    }
    updateIssuesById(store, optimisticUpdateIds, inputArgs)
    if (tabCounterDelta !== 0) changeIssuesTabCounter(tabCounterDelta)
  }
  const handleError = (error: Error) => {
    if (tabCounterDelta !== 0) changeIssuesTabCounter(-tabCounterDelta)
    if (onError) onError(error)
  }

  if (queryTarget) {
    return commitMutation<updateIssuesBulkActionsByQueryMutation>(environment, {
      mutation: updateIssuesBulkByQueryMutation,
      variables: {
        input: {...queryTarget, ...inputArgs},
      },
      updater,
      optimisticUpdater,
      onCompleted: response => onCompleted?.(response.updateIssuesBulkByQuery?.jobId ?? undefined),
      onError: handleError,
    })
  }

  return commitMutation<updateIssuesBulkActionsMutation>(environment, {
    mutation: updateIssuesBulkMutation,
    variables: {input: inputHash},
    updater,
    optimisticUpdater,
    onCompleted: response => onCompleted?.(response.updateIssuesBulk?.jobId ?? undefined),
    onError: handleError,
  })
}
